import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import type { DtoMeResponse } from "@porchpass/api-client";
import { useDealers } from "./DealersContext";

export type DealerUserInfoType = DtoMeResponse;

type DealerUserInfoContextType = {
  dealerUserInfo: DealerUserInfoType;
  isLoading: boolean;
} | null;

export const DealerUserInfoContext =
  createContext<DealerUserInfoContextType>(null);

export const DealerUserInfoProvider = ({
  children,
  dealerId,
}: {
  children: ReactNode;
  dealerId: string | undefined;
}) => {
  const navigate = useNavigate();
  const { dealerOptions } = useDealers();
  const [dealerUserInfo, setDealerUserInfo] = useState<DealerUserInfoType>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (
      !dealerId ||
      dealerId === "finding-user-dealer" ||
      dealerId === "dashboard" ||
      !dealerOptions.length
    ) {
      return;
    }

    setIsLoading(true);

    const dealerUser = dealerOptions.find(
      (dealer) => dealer.dealer_id === dealerId,
    );
    if (dealerUser) {
      setDealerUserInfo(dealerUser);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      void navigate("/error", { state: { status: 403 }, replace: true });
    }
  }, [dealerId, dealerOptions]);

  return (
    <DealerUserInfoContext.Provider value={{ dealerUserInfo, isLoading }}>
      {children}
    </DealerUserInfoContext.Provider>
  );
};

export const useDealerUserInfo = () => {
  const context = useContext(DealerUserInfoContext);
  if (!context) {
    throw new Error(
      "useDealerUserInfo must be used within a DealerUserInfoProvider",
    );
  }
  return context;
};
